import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosInstance';
import {
  SYSTEM_ALLOWED_MIME_TYPES,
  EXTENSION_TO_MIME,
} from '../../../constants/formCrafter.constant';

/**
 * useFilesApi Hook
 *
 * Unified hook for all file operations using the centralized file module.
 * This replaces the legacy useStagedFilesApi and useSubFormFilesApi hooks.
 *
 * The file module uses a 2-phase upload pattern:
 * - Phase 1: Upload file → status: STAGED
 * - Phase 2: Attach to entity → status: AVAILABLE
 *
 * All files use the same endpoints regardless of whether they're
 * "staged" (not yet attached) or "server" (attached to entity).
 *
 * Endpoints:
 * - POST /api/v1/files/upload - Upload file
 * - GET /api/v1/files/:id - Get file info
 * - GET /api/v1/files/:id/download - Download file
 * - GET /api/v1/files/:id/preview - Preview file
 * - DELETE /api/v1/files/:id - Soft delete file
 */
export const useFilesApi = () => {
  const [state, setState] = useState({
    uploading: false,
    progress: 0,
    error: null,
  });

  /**
   * Validate a file before upload
   */
  const validateFile = useCallback((file, maxSizeMB = 10) => {
    if (!file) {
      return { isValid: false, error: 'No file provided', warnings: [] };
    }

    const warnings = [];
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds ${maxSizeMB}MB limit`,
        warnings,
      };
    }

    // Use system-wide allowed extensions
    const supportedExtensions = Object.keys(EXTENSION_TO_MIME).map((ext) =>
      ext.replace('.', '')
    );
    const genericMimeTypes = [
      'application/octet-stream',
      'application/x-download',
      'binary/octet-stream',
    ];
    const supportedMimeTypes = [
      ...SYSTEM_ALLOWED_MIME_TYPES,
      ...genericMimeTypes,
    ];

    const fileName = file.name.toLowerCase();
    const fileExtension = fileName.split('.').pop();
    const mimeType = (file.type || '').toLowerCase();

    const isMimeSupported = supportedMimeTypes.some((type) =>
      mimeType.includes(type)
    );
    const isExtensionSupported = supportedExtensions.includes(fileExtension);

    if (!isMimeSupported && !isExtensionSupported) {
      return {
        isValid: false,
        error: `Unsupported file type: .${fileExtension} (${mimeType})`,
        warnings,
      };
    }

    return { isValid: true, error: null, warnings };
  }, []);

  /**
   * Upload a file to the centralized file module.
   * File will have status STAGED until attached to an entity.
   *
   * @param {File} file - File to upload
   * @param {Object} options - Upload options
   * @param {number} options.subformId - Subform ID (for field context)
   * @param {number} options.formId - Form ID (optional, for disambiguation)
   * @param {string} options.fieldId - Field ID (for field context)
   * @param {string} options.context - Context type (submission, comment, etc.)
   * @param {number} options.maxFiles - Maximum files allowed for this field
   * @param {Function} options.onProgress - Progress callback
   * @returns {Object|null} Uploaded file info or null on error
   */
  const uploadFile = useCallback(
    async (
      file,
      {
        subformId,
        fieldId,
        formId,
        context = 'submission',
        maxFiles = 1,
        onProgress,
      } = {}
    ) => {
      if (!file) {
        toast.error('Please select a file to upload', {
          position: 'top-center',
        });
        return null;
      }

      if (!subformId || !fieldId) {
        toast.error('Missing subformId or fieldId for file upload', {
          position: 'top-center',
        });
        return null;
      }

      // Validate file
      const validation = validateFile(file);
      if (!validation.isValid) {
        toast.error(validation.error, { position: 'top-center' });
        return null;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('subform_id', subformId);
      formData.append('field_id', fieldId);
      formData.append('context', context);
      formData.append('max_files', maxFiles);
      if (formId) {
        formData.append('form_id', formId);
      }

      setState((prev) => ({
        ...prev,
        uploading: true,
        progress: 0,
        error: null,
      }));

      try {
        const response = await axiosInstance.post(
          '/api/v1/files/upload',
          formData,
          {
            headers: { 'Content-Type': undefined },
            timeout: 120000,
            onUploadProgress: (event) => {
              const total = event.total || event.loaded || 1;
              const progress = Math.round((event.loaded * 100) / total);
              setState((prev) => ({ ...prev, progress }));
              if (onProgress) {
                onProgress(progress);
              }
            },
          }
        );

        // Extract file data from response
        const uploadedFile = response.data?.data || response.data;

        // Normalize file structure
        const normalizedFile = {
          id: uploadedFile.file_id || uploadedFile.id,
          name: uploadedFile.original_name,
          size: Number(uploadedFile.size || uploadedFile.file_size || 0),
          type: uploadedFile.mime_type,
          status: uploadedFile.status || 'STAGED',
          formId,
          subformId,
          fieldId,
          preview_url: uploadedFile.preview_url,
          expires_at: uploadedFile.expires_at,
        };

        // Show 100% completion briefly
        setState((prev) => ({ ...prev, progress: 100 }));
        await new Promise((resolve) => setTimeout(resolve, 300));

        // Reset to idle state
        setState({
          uploading: false,
          progress: 0,
          error: null,
        });

        return normalizedFile;
      } catch (error) {
        const errorMessage =
          error.response?.data?.error?.message ||
          error.response?.data?.message ||
          error.message ||
          'File upload failed';

        setState({
          uploading: false,
          progress: 0,
          error: errorMessage,
        });

        toast.error(errorMessage, { position: 'top-center' });
        return null;
      }
    },
    [validateFile]
  );

  // ================================================================
  // FILE DELETION API
  //
  // Two deletion contexts exist, each with a dedicated endpoint:
  //
  // 1. deleteFile(fileId)
  //    - For STAGED (unattached) files only.
  //    - Calls: DELETE /api/v1/files/:id
  //    - Use when: Cancelling an upload, removing a file before form submission.
  //
  // 2. deleteAttachedFiles(fileIds, options?)
  //    - For ATTACHED files (linked to EndUserFilledForm / Draft).
  //    - Calls: POST /api/endUserFilledForms/files/delete
  //    - Removes from EndUserFilledFormAttachment, DraftAttachment (if draftId),
  //      and soft-deletes the file records - all in one atomic transaction.
  //    - Use when: User confirms deletion of a file that was already submitted or drafted.
  //
  // Rule of thumb: If the file was uploaded during the CURRENT edit session
  // and hasn't been submitted yet, use deleteFile. If it came from the server
  // (existingFiles / serverFiles), use deleteAttachedFiles.
  // ================================================================

  /**
   * Delete a staged/unattached file (soft delete via file service).
   * Use this for files that are only in STAGED status and not yet attached.
   *
   * @param {number} fileId - File ID to delete
   * @returns {boolean} Success status
   */
  const deleteFile = useCallback(async (fileId) => {
    if (!fileId) return false;

    try {
      await axiosInstance.delete(`/api/v1/files/${fileId}`);
      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        'Failed to delete file';
      console.error('Error deleting staged file:', errorMessage);
      return false;
    }
  }, []);

  /**
   * Delete attached files - removes from EndUserFilledFormAttachment,
   * DraftAttachment (if draftId provided), and soft-deletes the file records.
   * All operations are wrapped in a single database transaction.
   *
   * Use this for files that are already attached to a filled form or draft.
   *
   * @param {Array<number>} fileIds - Array of file IDs to delete
   * @param {Object} [options] - Optional scoping parameters
   * @param {number} [options.endUserFilledFormId] - Scope to specific filled form
   * @param {number} [options.draftId] - Also delete from draft attachments
   * @returns {boolean} Success status
   */
  const deleteAttachedFiles = useCallback(async (fileIds, options = {}) => {
    if (!fileIds || !Array.isArray(fileIds) || fileIds.length === 0)
      return false;

    try {
      await axiosInstance.post('/api/endUserFilledForms/files/delete', {
        deleted_file_ids: fileIds,
        endUserFilledFormId: options.endUserFilledFormId || undefined,
        draftId: options.draftId || undefined,
      });
      return true;
    } catch (error) {
      const errorMessage =
        error.response?.data?.error?.message ||
        error.response?.data?.message ||
        error.message ||
        'Failed to delete files';
      console.error('Error deleting attached files:', errorMessage);
      return false;
    }
  }, []);

  /**
   * Download a file
   *
   * @param {number} fileId - File ID
   * @param {string} filename - Filename for download
   */
  const downloadFile = useCallback(async (fileId, filename) => {
    if (!fileId) return;

    try {
      const response = await axiosInstance.get(
        `/api/v1/files/${fileId}/download`,
        {
          responseType: 'blob',
        }
      );

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `file_${fileId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file', { position: 'top-center' });
    }
  }, []);

  /**
   * Get file info
   *
   * @param {number} fileId - File ID
   * @returns {Object|null} File info or null
   */
  const getFileInfo = useCallback(async (fileId) => {
    if (!fileId) return null;

    try {
      const response = await axiosInstance.get(`/api/v1/files/${fileId}`);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error getting file info:', error);
      return null;
    }
  }, []);

  /**
   * Build preview URL for a file
   *
   * @param {number} fileId - File ID
   * @returns {string} Preview URL
   */
  const getPreviewUrl = useCallback((fileId) => {
    if (!fileId) return null;
    const baseUrl = process.env.REACT_APP_SERVER_URL || window.location.origin;
    return `${baseUrl}/api/v1/files/${fileId}/preview`;
  }, []);

  const resetState = useCallback(() => {
    setState({
      uploading: false,
      progress: 0,
      error: null,
    });
  }, []);

  return {
    // State
    uploading: state.uploading,
    progress: state.progress,
    error: state.error,

    // Functions
    uploadFile,
    deleteFile,
    deleteAttachedFiles,
    downloadFile,
    getFileInfo,
    getPreviewUrl,
    validateFile,
    resetState,
  };
};

export default useFilesApi;
